import { Component } from '@theme/component';

/**
 * One product, several conditions, one big stage.
 *
 * Icon tabs switch which frame is showing; the frames are stacked and cross-fade
 * so the stage never jumps in height.
 *
 * Progressive enhancement: Liquid marks the first state active, so without JS
 * the visitor sees the first frame and its copy. The other frames are stacked
 * underneath at opacity 0 and only become reachable once this upgrades.
 *
 * @extends {Component<{
 *   list: HTMLElement,
 *   tab: HTMLButtonElement[],
 *   frame: HTMLElement[],
 *   copy: HTMLElement[],
 * }>}
 */
class SanyStates extends Component {
  requiredRefs = ['tab', 'frame', 'copy'];

  #index = 0;

  connectedCallback() {
    super.connectedCallback();
    const initial = this.refs.tab.findIndex((b) => b.getAttribute('aria-selected') === 'true');
    this.select(initial < 0 ? 0 : initial);
  }

  /** @param {number} index */
  select(index) {
    const { tab, frame, copy } = this.refs;
    const count = tab.length;
    if (!count) return;
    this.#index = ((index % count) + count) % count;

    tab.forEach((button, i) => {
      const active = i === this.#index;
      button.setAttribute('aria-selected', String(active));
      button.tabIndex = active ? 0 : -1;
    });
    frame.forEach((el, i) => {
      const active = i === this.#index;
      el.classList.toggle('is-active', active);
      el.setAttribute('aria-hidden', String(!active));
    });
    copy.forEach((el, i) => {
      el.hidden = i !== this.#index;
    });
  }

  /** Bound on each tab button. Horizon rebinds event.target to that button. */
  onTab(event) {
    const button = /** @type {HTMLElement} */ (event.target);
    const index = this.refs.tab.indexOf(/** @type {HTMLButtonElement} */ (button));
    if (index >= 0) this.select(index);
  }

  /** Arrow keys on the tablist, per the WAI-ARIA tabs pattern. */
  onKeydown(event) {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (step === undefined) {
      if (event.key === 'Home') this.select(0);
      else if (event.key === 'End') this.select(this.refs.tab.length - 1);
      else return;
    } else {
      this.select(this.#index + step);
    }
    event.preventDefault();
    this.refs.tab[this.#index]?.focus();
  }

}

if (!customElements.get('sany-states')) {
  customElements.define('sany-states', SanyStates);
}
